/**
 * Language Switcher for Auth Pages
 *
 * A simplified language switcher that doesn't require teamId/uid.
 * Uses localStorage with a generic key that will be migrated to user-specific
 * storage after login.
 */

import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import i18n from '@/app/i18n/i18n';

const STORAGE_KEY = 'modcar-ui-lang';

type UILanguage = 'nl' | 'en';

function setStoredLanguage(lang: UILanguage) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage not available
  }
}

export function AuthLanguageSwitcher() {
  const { t } = useTranslation('common');
  const currentLang = (i18n.language || 'nl') as UILanguage;

  const handleLanguageChange = (lang: UILanguage) => {
    void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    setStoredLanguage(lang);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 rounded-xl"
          aria-label={t('accessibility.changeLanguage')}
        >
          <Globe className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8}>
        <DropdownMenuItem
          onClick={() => handleLanguageChange('nl')}
          className={currentLang === 'nl' ? 'bg-muted' : ''}
        >
          Nederlands (NL)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleLanguageChange('en')}
          className={currentLang === 'en' ? 'bg-muted' : ''}
        >
          English (EN)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
